<template>
  <div class="container">
    <!-- 添加 AddMyFavorite 組件 -->
    <AddMyFavorite ref="addFavoriteRef" />

    <!-- 標題和分頁按鈕並排顯示 -->
    <div class="title-pagination-container">
      <h2 class="text-white">
        {{ getTitle }}
      </h2>
      
      <!-- 分頁按鈕 - 在手機模式下顯示在標題旁邊 -->
      <div class="mobile-pagination" v-if="totalPages > 1">
        <button 
          class="pagination-btn-mobile prev"
          :class="{ 'disabled': !hasPrevious }"
          @click="prevPage"
          :disabled="!hasPrevious"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <span class="page-info-mobile">{{ currentPage }}/{{ totalPages }}</span>
        <button 
          class="pagination-btn-mobile next"
          :class="{ 'disabled': !hasNext }"
          @click="nextPage"
          :disabled="!hasNext"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- 歌曲列表 -->
    <ul class="list-group">
      <li 
        v-for="song in songs" 
        :key="song.id" 
        class="list-group-item" 
        @mouseover="hoveredSong = song" 
        @mouseleave="hoveredSong = null" 
        @click="selectSong(song)"
        @dblclick="addToPlaylistAndConfirm(song)"
        :class="{ 
          'hovered': hoveredSong === song, 
          'selected': selectedSong && selectedSong.id === song.id 
        }"
      >
        <div class="row align-items-center">
          <!-- 視頻預覽區域 -->
          <div class="col-md-4">
            <div class="video-wrapper1">
              <!-- 視頻播放器 -->
              <video
                v-if="canPlayVideo(song.synopsis)"
                :key="song.id"
                :src="videoUrls[song.id]"
                :data-original-filename="song.synopsis"
                class="preview-video"
                preload="metadata"
                muted
                :controls="false"
                autoplay
                loop
                @error="handleVideoError"
              >
                <source 
                  :src="videoUrls[song.id]"
                  :type="getVideoType(song.synopsis)"
                >
              </video>
              <!-- 備用顯示 -->
              <div v-else class="video-fallback">
                <img 
                  v-if="song.synopsis && song.synopsis.includes('youtube.com')"
                  :src="getYoutubeThumbnail(song.synopsis)"
                  class="preview-image"
                  alt="YouTube 預覽圖"
                >
                <img 
                  v-else-if="song.album"
                  :src="song.album"
                  class="preview-image"
                  alt="專輯封面"
                >
                <div v-else class="format-notice">
                  暫無預覽圖片
                </div>
              </div>
            </div>
          </div>

          <!-- 歌曲信息區域 -->
          <div class="col-md-6">
            <div class="text-white">
              <div class="song-info">
              <div class="song-class">分類: {{ song.songclass }}</div>
              <div class="song-name">歌名: {{ song.songname }}</div>
              </div>
           </div>
          </div>
          <!-- 價格信息區域 -->
          <div class="col-md-6">
            <div v-if="hoveredSong === song" class="popover bg-light p-2 rounded shadow">
              <!-- <p class="mb-0">通路＋版權費：{{ song.songChargeA }}+{{ song.songChargeB }}元</p> -->
            </div>
          </div>
        </div>
      </li>
    </ul>

    <!-- 確認點歌彈窗 -->
    <div v-if="showModal" class="modal" style="z-index: 999999 !important; position: fixed !important; top: 0 !important; left: 0 !important; width: 100vw !important; height: 100vh !important; background-color: rgba(0, 0, 0, 0.5) !important; display: flex !important; justify-content: center !important; align-items: center !important;">
      <!-- <div class="modal-content bg-info"> -->
      <div class="modal-content" style="z-index: 1000000 !important; position: relative !important; background-color: #6488b0 !important; padding: 20px !important; border-radius: 5px !important; width: 80% !important; max-width: 500px !important;">
        <span class="close" @click="closeModal">×</span>
        <p>歌曲名稱: {{ selectedSong.songname }}</p>
        <!-- <p class="mb-0">通路＋版權費：本曲收費{{ selectedSong.songChargeA }}+{{ selectedSong.songChargeB }}元</p>
        <p>播放後收費，播放前可取消！</p> -->
        <button @click="addToPlaylistAndConfirm(selectedSong)">確認</button>
      </div>
    </div>

    <!-- 分頁按鈕 - 在桌面模式下顯示在底部 -->
    <div class="pagination-container desktop-pagination" v-if="totalPages > 1">
      <button 
        class="pagination-btn prev"
        :class="{ 'disabled': !hasPrevious }"
        @click="prevPage"
        :disabled="!hasPrevious"
      >
        <i class="fas fa-chevron-left"></i> 上頁
      </button>
      <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
      <button 
        class="pagination-btn next"
        :class="{ 'disabled': !hasNext }"
        @click="nextPage"
        :disabled="!hasNext"
      >
        下頁 <i class="fas fa-chevron-right"></i>
      </button>
      
    </div>
    <hr class="page-end-line">    
    <div class="bottom-spacer"></div>
    
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { usePlaylistStore } from '../store/usePlaylistStore';
import { useUserStore } from '@/store/useUserStore';
import AddMyFavorite from './addmyfavorite.vue';  // 導入 AddMyFavorite 組件

// 創建一個配置了的 axios 實例
// 動態設置 API base URL - 強制使用 ordersong API 路徑
const axiosInstance = axios.create({
  baseURL: '/ordersong/api',  // 強制使用 ordersong API 路徑
  timeout: 5000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// 定義 props
const props = defineProps({
  param: {
    type: String,
    required: true
  }
});

// 計算標題
const getTitle = computed(() => {
  const titles = {
    'AA': '排行歌曲-國語',
    'AB': '排行歌曲-台語',
    'BA': '新進歌曲-國語',
    'BB': '新進歌曲-台語',
    'CA': '熱唱歌曲-國語',
    'CB': '熱唱歌曲-台語',
    'DA': '我的最愛DA'
  };
  return titles[props.param] || '精選歌曲';
});

// 狀態管理
const songs = ref([]);
const hasNext = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const hasPrevious = ref(false);
const hoveredSong = ref(null);
const selectedSong = ref(null);
const showModal = ref(false);
const videoUrls = ref({});
const loading = ref(false);
const error = ref(null);
const userStore = useUserStore();
// 從 store 中獲取播放列表相關方法
const { playlist, addSong, removeSong } = usePlaylistStore();

const addFavoriteRef = ref(null);

// 獲取歌曲列表
const fetchSongs = async () => {
  try {
    console.log('Fetching featured songs...');
    loading.value = true;
    error.value = null;
    
    // 確保使用相對路徑
    const url = `/featured_songs/?featured_type=${encodeURIComponent(props.param)}&page=${currentPage.value}`;
    console.log('Request URL:', url);
    
    const response = await axiosInstance.get(url, {
      timeout: 60000  // 增加到 60 秒
    });
    
    console.log('Response:', response.data);
    
    // 更新狀態
    songs.value = response.data.results || [];
    currentPage.value = response.data.current_page || 1;
    totalPages.value = response.data.total_pages || 1;
    hasNext.value = response.data.has_next || false;
    hasPrevious.value = response.data.has_previous || false;
    
    // 初始化視頻
    await initializeVideos(songs.value);
    
    loading.value = false;
  } catch (error) {
    console.error('Error fetching songs:', error);
    error.value = error.message || 'Failed to fetch songs';
    loading.value = false;
  }
};

// 視頻相關工具函數
const canPlayVideo = (filename) => {
  if (!filename) return false;
  if (filename.includes('youtube.com')) return false;
  const ext = filename.split('.').pop().toLowerCase();
  const supportedFormats = ['mp4', 'webm', 'ogg', 'mkv'];
  return supportedFormats.includes(ext);
};

const getVideoType = (filename) => {
  if (!filename) return '';
  const ext = filename.split('.').pop().toLowerCase();
  const mimeTypes = {
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'ogg': 'video/ogg',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo'
  };
  return mimeTypes[ext] || 'video/mp4';
};

// 初始化視頻
const initializeVideos = async (songsList) => {
  for (const song of songsList) {
    if (song.synopsis) {
      videoUrls.value[song.id] = await getVideoUrl(song.synopsis, song.id);
    }
  }
};

// 獲取視頻URL
const getVideoUrl = async (filename, songId) => {
  if (!filename) return '';
  try {
    const encodedFilename = encodeURIComponent(filename);
    const synopsisUrl = `${import.meta.env.VITE_API_URL}/media/songs/synopsis/${encodedFilename}`;
    
    try {
      const response = await fetch(synopsisUrl, { method: 'HEAD' });
      return response.ok 
        ? synopsisUrl 
        : `${import.meta.env.VITE_API_URL}/media/songs/files/${encodedFilename}`;
    } catch (error) {
      return `${import.meta.env.VITE_API_URL}/media/songs/files/${encodedFilename}`;
    }
  } catch (error) {
    console.error('Error creating video URL:', error);
    return '';
  }
};

// 視頻錯誤處理
const handleVideoError = async (event) => {
  const video = event.target;
  console.error('視頻加載錯誤:', video.error);
  
  try {
    const currentUrl = video.currentSrc;
    if (currentUrl.includes('/synopsis/')) {
      const newUrl = currentUrl.replace('/synopsis/', '/files/');
      video.src = newUrl;
    }
  } catch (error) {
    console.error('切換視頻源時出錯:', error);
  }
};

// YouTube 縮略圖
const getYoutubeThumbnail = (url) => {
  const videoId = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/user\/\S+|\/ytscreeningroom\?v=|\/sandalsResorts#\w\/\w\/.*\/))([^\/&\?]{10,12})/);
  return videoId ? `https://img.youtube.com/vi/${videoId[1]}/hqdefault.jpg` : '';
};

// 選擇歌曲
const selectSong = (song) => {
  selectedSong.value = song;
  showModal.value = true;
};

// 關閉確認彈窗
const closeModal = () => {
  showModal.value = false;
};

// 添加到播放列表
const addToPlaylistAndConfirm = async (song) => {
  try {
    addSong({
      id: song.id,
      songname: song.songname,
      filename: song.filename,
      stateA: false,
      stateB: false,
    });
    
    // 準備請求數據
    const requestData = {
      songsname: song.id,
    };
    
    // 如果用戶已登入，添加 customerno；否則允許未登入用戶點歌（customerno 為 null）
    const customerId = userStore.user?.customer_id;
    if (customerId) {
      // 從 'C104' 中提取數字 104
      const numericUserId = parseInt(customerId.replace('C', ''));
      if (!isNaN(numericUserId)) {
        requestData.customerno = numericUserId;
      } else {
        console.warn('無法解析用戶 ID:', customerId);
      }
    } else {
      console.log('用戶未登入，將創建無用戶 ID 的歌曲請求');
    }
    
    await axiosInstance.post('/songsrequest/create/', requestData);
    console.log('FeaturedSongs 歌曲請求:', requestData);

    showModal.value = false;
  } catch (error) {
    console.error('Error adding song to playlist:', error);
  }
};

// 分頁方法
const nextPage = async () => {
  if (hasNext.value) {
    console.log('切換到下一頁，當前頁碼:', currentPage.value);
    currentPage.value++; // 增加頁碼
    console.log('新頁碼:', currentPage.value);
    await fetchSongs();
  }
};

const prevPage = async () => {
  if (hasPrevious.value) {
    console.log('切換到上一頁，當前頁碼:', currentPage.value);
    currentPage.value--; // 減少頁碼
    console.log('新頁碼:', currentPage.value);
    await fetchSongs();
  }
};

// 添加到最愛
const addToFavorite = (songId) => {
  console.log('嘗試添加歌曲到最愛，songId:', songId);
  if (addFavoriteRef.value) {
    addFavoriteRef.value.openAddFavorite(songId);
  } else {
    console.error('addFavoriteRef is null');
  }
};

// 組件掛載時獲取數據
onMounted(() => {
  fetchSongs();
});
</script>

<style scoped>
.container {
  padding-bottom: 60px;  /* 減少基本間距 */
  height: calc(100vh - 60px);  /* 設置容器高度 */
  overflow-y: auto;  /* 允許內容滾動 */
  -webkit-overflow-scrolling: touch;  /* 提供更好的移動端滾動體驗 */
}

.bottom-spacer {
  display: none;
}

.list-group-item {
  margin-bottom: 0.5rem;
  background-color: rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.5rem;
}

.video-wrapper1 {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: #000;
  overflow: hidden;
  margin-bottom: 0rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.preview-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
}

.row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}

.col-md-4 {
  flex: 0 0 33.333333%;
  max-width: 33.333333%;
}

.col-md-6 {
  flex: 0 0 50%;
  max-width: 50%;
}

.text-white {
  color: white !important;
}

.modal {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  background-color: rgba(0, 0, 0, 0.3) !important;
  z-index: 10000 !important; /* 合理的 z-index 值 */
}

.modal-content {
  background-color: #6488b0 !important;
  padding: 20px !important;
  border-radius: 5px !important;
  width: 80% !important;
  max-width: 500px !important;
  position: relative !important;
  z-index: 10001 !important; /* 比 modal 背景更高 */
}

.video-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 8px;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.format-notice {
  color: white;
  text-align: center;
  padding: 1rem;
  font-size: 0.9rem;
  opacity: 0.8;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.8rem;
  border: none;
  border-radius: 15px;
  background: linear-gradient(145deg, #007bff, #0056b3);
  color: white;
  font-weight: normal;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pagination-btn:hover:not(.disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.pagination-btn.disabled {
  background: #e0e0e0;
  cursor: not-allowed;
  opacity: 0.7;
}

.page-info {
  font-size: 0.9rem;
  color: #666;
  padding: 0.3rem 0.8rem;
  background-color: #f8f9fa;
  border-radius: 12px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.fas {
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .col-md-4, .col-md-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .list-group-item {
    padding: 0.25rem;
    max-height: 400px;
    overflow: hidden;
  }

  .video-wrapper {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .container {
    height: calc(100vh - 350px);
    padding-bottom: 200px;
  }
  .list-group-item:last-child {
    margin-bottom: 200px;
  }
  
  .video-wrapper1 {
    /* max-width: 240px; */
    margin-top: 0.5rem;
    padding-top: 0.25rem;
    width: 100%;
    max-width: 100%;
  }

  .list-group-item {
    max-height: 320px; /* 確保在超小屏幕上也應用相同的高度減少 */
    padding-bottom: 0.15rem; /* 減少底部內邊距 */
  }
  
  .preview-image {
    width: 100% !important; /* 放大到 150% */
    max-width: 100% !important;
    object-fit: cover !important; /* 確保圖片填滿容器 */
    object-position: center top !important; /* 靠上對齊 */
    margin-top: 0rem !important; /* 進一步向上調整 */
  }
  
  .song-info {
    text-align: left;
    padding-left: 10px;
    padding-top: 10px;
  }
  
  .song-class, .song-name {
    margin-top: 10px;
  }

  /* .video-fallback {
    overflow: hidden !important; 
  } */
}

.btn-favorite {
  padding: 5px 15px;
  background-color: #ff69b4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.btn-favorite:hover {
  background-color: #ff1493;
}

/* 標題和分頁按鈕容器 */
.title-pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

/* 手機模式下的分頁按鈕 */
.mobile-pagination {
  display: none;
  align-items: center;
}

.pagination-btn-mobile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: #007bff;
  color: white;
  font-size: 0.8rem;
  margin: 0 3px;
  padding: 0;
}

.pagination-btn-mobile:disabled {
  background: #e0e0e0;
  cursor: not-allowed;
}

.page-info-mobile {
  font-size: 0.8rem;
  color: white;
  margin: 0 5px;
}

/* 桌面模式下的分頁按鈕 */
.desktop-pagination {
  display: flex;
}

/* 響應式設計 */
@media (max-width: 768px) {

  .container {
    height: calc(100vh - 200px);  /* 調整手機模式下的容器高度 */
    padding-bottom: 150px;  /* 增加底部間距 */
    margin-bottom: 50px;  /* 額外的底部邊距 */
  }
  
  .list-group-item:last-child {
    margin-bottom: 150px;  /* 確保最後一個項目有足夠的底部間距 */
  }

  .title-pagination-container {
    flex-direction: row;
    align-items: center;
  }
  
  .mobile-pagination {
    display: flex;
  }
  
  /* 在手機模式下保留底部分頁按鈕，但使其更緊湊 */
  .pagination-container {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  
  .pagination-btn {
    padding: 0.2rem 0.5rem;
    font-size: 0.8rem;
  }
  
  .page-info {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
  }
  
  h2 {
    font-size: 1.5rem;
    margin-right: 10px;
    margin-bottom: 0;
  }
}

/* 超小屏幕設備 */
@media (max-width: 576px) {
  .title-pagination-container {
    flex-wrap: wrap;
  }
  
  h2 {
    font-size: 1.2rem;
    margin-bottom: 5px;
  }
  
  .mobile-pagination {
    margin-left: auto;
  }
  
  /* 在超小屏幕上進一步縮小底部分頁按鈕 */
  .pagination-btn {
    padding: 0.15rem 0.4rem;
    font-size: 0.75rem;
  }
  
  .page-info {
    font-size: 0.75rem;
    padding: 0.15rem 0.4rem;
  }
}

.page-end-line {
  border: 1;
  height: 10px;
  background: rgb(50, 10, 10);
  margin-bottom: -13rem 0;
}
</style>
